import {
  isModuleFlagEnabled,
  isOptInFlagEnabled,
  type ModuleKey,
} from '@bill-alps/backend/features/companies/modules';

/**
 * Deploy-wide module kill switches, baked in at build time. Set a var to
 * 'false' to hide the module from the whole deployment; unset means enabled.
 *
 * The matching Convex vars (same names without `VITE_`) must be set too — this
 * one only hides the UI, the backend guard lives in `_lib/modules.ts`.
 *
 * Vite inlines `import.meta.env` statically, so every var is read literally
 * here rather than looked up by key.
 */
export const MODULE_AVAILABLE: Record<ModuleKey, boolean> = {
  invoicing: isModuleFlagEnabled(import.meta.env.VITE_FEATURE_INVOICING),
  payroll: isModuleFlagEnabled(import.meta.env.VITE_FEATURE_PAYROLL),
  accounting: isModuleFlagEnabled(import.meta.env.VITE_FEATURE_ACCOUNTING),
};

/**
 * Closing an exercise and the annual accounts (route
 * `/accounting/annual-accounts`, the PDF and its versions).
 * Controlled by `VITE_FEATURE_FINANCIAL_STATEMENTS` while the rest of the
 * accounting module ships. The Convex half (`FEATURE_FINANCIAL_STATEMENTS`)
 * closes the matching API. Independent of `VITE_FEATURE_ACCOUNTING`, which
 * kills the whole module.
 */
export const STATEMENT_REPORTING_AVAILABLE = isModuleFlagEnabled(
  import.meta.env.VITE_FEATURE_FINANCIAL_STATEMENTS,
);

/**
 * Double-entry ledger (chart of accounts, journal, VAT returns, closing) —
 * routes under `/accounting/*` and the accounting tab of `/company`.
 * Deployment-gated behind `VITE_FEATURE_LEDGER`; the Convex half is
 * `FEATURE_LEDGER`.
 *
 * Opt-in, unlike the module kill switches above: only the literal `true`
 * ships the UI, so a build that never heard of the var keeps it hidden.
 * Both halves must be set — the Vite one here and `FEATURE_LEDGER` on the
 * Convex deployment — or the accounting tab reports the ledger unavailable.
 */
export const LEDGER_AVAILABLE = isOptInFlagEnabled(
  import.meta.env.VITE_FEATURE_LEDGER,
);

/**
 * Invoice import from another software (`/bills/import`, the "Importer des
 * factures" button of the invoice list). Opt-in like the ledger: only the
 * literal `VITE_FEATURE_INVOICE_IMPORT=true` ships the UI. The Convex half
 * (`FEATURE_INVOICE_IMPORT`) opens the API; both must be set.
 */
export const INVOICE_IMPORT_AVAILABLE = isOptInFlagEnabled(
  import.meta.env.VITE_FEATURE_INVOICE_IMPORT,
);

/**
 * Collective agreements (CCT/GAV) on the user side: the "manage agreements"
 * block of the sector card on `/company` (salaries tab), the agreement and
 * wage-category fields plus the assessments panel on the employee form, and
 * the advisory CCT banners on payslips and absences. Opt-in: only the literal
 * `VITE_FEATURE_CCT=true` ships them, so production hides the whole CCT
 * surface until it is deliberately switched on; dev (`.env.local`), the
 * sandbox image and the e2e suite set it. UI only — the admin compliance
 * pages, the API and the stored fields are untouched, there is no Convex
 * half to pair with.
 */
export const CCT_AVAILABLE = isOptInFlagEnabled(
  import.meta.env.VITE_FEATURE_CCT,
);

/**
 * Cumulative AC ceiling over the calendar year: the "reprise des cumuls" card
 * on the employee form (salary card) and the incomplete-year banner on draft
 * payslips. Opt-in: only the literal `VITE_FEATURE_CUMULATIVE_CEILINGS=true`
 * ships them. The Convex half is `FEATURE_CUMULATIVE_CEILINGS`, which switches
 * the engine and the validation guard; with it off the banner query reports
 * `enabled: false` and nothing shows even when this one is on.
 */
export const CUMULATIVE_CEILINGS_AVAILABLE = isOptInFlagEnabled(
  import.meta.env.VITE_FEATURE_CUMULATIVE_CEILINGS,
);

/** Opt-in, unlike the module kill switches: only the literal "true" shows the card. */
export function isSwissdecEmployerIdentifiersAvailable(
  raw: string | undefined,
): boolean {
  return isOptInFlagEnabled(raw);
}

/**
 * Swissdec employer identifiers card on `/company` (salaries tab), shown only
 * when `VITE_SWISSDEC_EMPLOYER_IDENTIFIERS_AVAILABLE=true` is baked into the
 * build. The ELM declaration flow that consumes those numbers has not
 * shipped, so production leaves the var unset; dev (`.env.local`), the
 * sandbox image and the e2e suite set it. UI only — the fields stay writable
 * through the API, there is no Convex half to pair with.
 */
export const SWISSDEC_EMPLOYER_IDENTIFIERS_AVAILABLE =
  isSwissdecEmployerIdentifiersAvailable(
    import.meta.env.VITE_SWISSDEC_EMPLOYER_IDENTIFIERS_AVAILABLE,
  );

/**
 * Sandbox build (`VITE_APP_ENV=sandbox`): the demo deployment where every
 * company is Business and nothing is billed. Hides the plan, billing and
 * fiduciaire-plan surfaces; the Convex half is `SANDBOX_MODE` (see
 * `_lib/sandbox.ts`), which is what actually closes checkout and email.
 */
export const IS_SANDBOX = import.meta.env.VITE_APP_ENV === 'sandbox';

export function isDevProdImportFrontendMode(
  isViteDevelopment: boolean,
  appEnv: string | undefined,
): boolean {
  return isViteDevelopment && appEnv === 'dev-prod-import';
}

/** Local Vite UI backed by Convex DEV and authenticated with Clerk PROD. */
export const IS_DEV_PROD_IMPORT = isDevProdImportFrontendMode(
  import.meta.env.DEV,
  import.meta.env.VITE_APP_ENV,
);

/** UI surfaces shared by the sandbox and local PROD-data copy workflow. */
export const IS_DATA_IMPORT = IS_SANDBOX || IS_DEV_PROD_IMPORT;
export const IS_SIGN_UP_DISABLED = IS_DATA_IMPORT;
export const DATA_IMPORT_SETUP_PATH = IS_DEV_PROD_IMPORT
  ? '/dev/prod-import'
  : '/sandbox/setup';

/** Dated LAA history and salary allocation, independent of cumulative AC. */
export const LAA_MUTATIONS_AVAILABLE = isOptInFlagEnabled(
  import.meta.env.VITE_FEATURE_LAA_MUTATIONS,
);

/** Preparation UI, independent of the backend calculation switch. */
export const AVS_CUMULATIVE_AVAILABLE = isOptInFlagEnabled(
  import.meta.env.VITE_FEATURE_AVS_CUMULATIVE,
);
